import { PackageIcon, RefreshCwIcon, SaveIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { IncoTerms } from '@invenco/sales-interface/interfaces';
import { WarehouseCarrierServiceDTO } from '@invenco/warehousing-interface';

import { Button } from '@/components/core/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { SelectItem } from '@/components/core/select';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TagsFormField } from '@/components/forms/tags-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, Page, PageContent } from '@/components/layout';

import {
  DEFAULT_CARRIER_ACCOUNT_ID,
  DEFAULT_SHIPPING_METHOD_ID,
  getFormValues,
  renderShippingMethodName,
  useWarehouseCarrierServiceForm,
} from '../warehouse-carrier-service-form';
import { useWarehouseCarrierService } from './use-warehouse-carrier-service';

const WAREHOUSE_CARRIER_SERVICE_FORM_ID = 'warehouse-carrier-service-form';

export function WarehouseCarrierServiceDetails() {
  const { id } = useParams<{ id: string }>();
  const {
    warehouseCarrierService,
    isLoading,
    refetchWarehouseCarrierService,
    updateWarehouseCarrierService,
    isUpdatingWarehouseCarrierService,
  } = useWarehouseCarrierService(id);

  const {
    form,
    carriers,
    isLoadingCarriers,
    wmsInstances,
    isLoadingWmsInstances,
    carrierAccounts,
    isLoadingCarrierAccounts,
    filteredShippingMethods,
    isLoadingShippingMethods,
    selectedCarrier,
    selectedCarrierService,
    selectedCarrierId,
    selectedCarrierServiceId,
    selectedCarrierAccountId,
  } = useWarehouseCarrierServiceForm({
    defaultValues: getFormValues(),
    fallbackCarrierId: warehouseCarrierService?.carrierId,
  });

  // The carrier account is not stored on the DTO; it is resolved by matching
  // shippingMethodBookingAccountName against the accounts of the saved carrier.
  // Hydration therefore waits for that lookup and runs once per loaded entity,
  // via reset only - setValue would trigger the cascade that clears dependent fields.
  const [isHydrated, setIsHydrated] = useState(false);
  const hydratedFromRef = useRef<WarehouseCarrierServiceDTO>();
  useEffect(() => {
    if (!warehouseCarrierService || hydratedFromRef.current === warehouseCarrierService) return;
    if (warehouseCarrierService.shippingMethodBookingAccountName && isLoadingCarrierAccounts) {
      return;
    }
    hydratedFromRef.current = warehouseCarrierService;
    const carrierAccountId = carrierAccounts?.find(
      (carrierAccount) =>
        carrierAccount.name === warehouseCarrierService.shippingMethodBookingAccountName,
    )?.id;
    form.reset(getFormValues(warehouseCarrierService, carrierAccountId));
    setIsHydrated(true);
  }, [warehouseCarrierService, carrierAccounts, isLoadingCarrierAccounts, form]);

  const pageTitle = warehouseCarrierService
    ? [
        warehouseCarrierService.carrierServiceCode,
        warehouseCarrierService.signatureRequired ? 'SIG' : 'ATL',
        warehouseCarrierService.incoTerms,
      ]
        .filter(Boolean)
        .join('_')
    : undefined;

  const onSubmit = form.handleSubmit((data) => {
    void updateWarehouseCarrierService({
      wmsInstanceId: data.wmsInstanceId,
      carrierCode: selectedCarrier?.code,
      carrierServiceCode: selectedCarrierService?.code,
      // the shipping method field is disabled for default accounts, leaving its value undefined
      shippingMethodId:
        !data.shippingMethodId || data.shippingMethodId === DEFAULT_SHIPPING_METHOD_ID
          ? null
          : data.shippingMethodId,
      codes: [
        { code: data.primaryCode, isPrimary: true },
        ...data.secondaryCodes.map((code) => ({ code, isPrimary: false })),
      ],
      incoTerms: data.incoTerms,
      signatureRequired: data.signatureRequired === 'true',
    });
  });

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon
          parentUrl="/warehouse-carrier-services"
          parentTitle="Warehouse carrier services"
        >
          <PackageIcon />
        </HeaderIcon>
        <HeaderTitle>{pageTitle}</HeaderTitle>
        <HeaderActions>
          <Button
            type="submit"
            form={WAREHOUSE_CARRIER_SERVICE_FORM_ID}
            loading={isUpdatingWarehouseCarrierService}
            disabled={!isHydrated}
          >
            <SaveIcon />
            Save
          </Button>
          <Button
            size="icon"
            variant="outline"
            aria-label="Refresh"
            onClick={() => void refetchWarehouseCarrierService()}
            disabled={isLoading}
          >
            <RefreshCwIcon />
          </Button>
        </HeaderActions>
      </Header>
      <PageContent variant="split">
        <DetailMain>
          <Form {...form}>
            {/* Remounting on hydration stops Radix's hidden native selects from
                dispatching a value-clearing change event when a select's value and
                its items arrive in the same render (see SelectBubbleInput). */}
            <form
              key={String(isHydrated)}
              id={WAREHOUSE_CARRIER_SERVICE_FORM_ID}
              className="contents"
              onSubmit={onSubmit}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Details</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                  <SelectFormField
                    control={form.control}
                    name="wmsInstanceId"
                    label="WMS instance"
                    className="w-full min-w-0"
                    placeholder={
                      isLoadingWmsInstances ? 'Loading WMS instances' : 'Select WMS instance'
                    }
                    disabled={!isHydrated}
                  >
                    {wmsInstances?.map((wmsInstance) => (
                      <SelectItem key={wmsInstance.id} value={wmsInstance.id}>
                        {wmsInstance.code}
                      </SelectItem>
                    ))}
                  </SelectFormField>
                  <SelectFormField
                    control={form.control}
                    name="carrierId"
                    label="Carrier"
                    className="w-full min-w-0"
                    placeholder={isLoadingCarriers ? 'Loading carriers' : 'Select carrier'}
                    disabled={!isHydrated}
                  >
                    {carriers.map((carrier) => (
                      <SelectItem key={carrier.id} value={carrier.id}>
                        {carrier.name}
                      </SelectItem>
                    ))}
                  </SelectFormField>
                  <SelectFormField
                    control={form.control}
                    name="carrierServiceId"
                    label="Carrier service"
                    className="w-full min-w-0"
                    disabled={!isHydrated || !selectedCarrierId}
                  >
                    {selectedCarrier?.carrierServices?.map((carrierService) => (
                      <SelectItem key={carrierService.id} value={carrierService.id}>
                        {carrierService.name}
                      </SelectItem>
                    ))}
                  </SelectFormField>
                  <SelectFormField
                    control={form.control}
                    name="carrierAccountId"
                    label="Carrier account"
                    className="w-full min-w-0"
                    disabled={!isHydrated || !selectedCarrierId || isLoadingCarrierAccounts}
                  >
                    <SelectItem value={DEFAULT_CARRIER_ACCOUNT_ID}>Default</SelectItem>
                    {carrierAccounts?.map((carrierAccount) => (
                      <SelectItem key={carrierAccount.id} value={carrierAccount.id}>
                        {carrierAccount.name}
                      </SelectItem>
                    ))}
                  </SelectFormField>
                  <SelectFormField
                    control={form.control}
                    name="shippingMethodId"
                    label="Shipping method"
                    className="w-full min-w-0"
                    disabled={
                      !isHydrated ||
                      !selectedCarrierServiceId ||
                      selectedCarrierAccountId === DEFAULT_CARRIER_ACCOUNT_ID ||
                      isLoadingShippingMethods
                    }
                  >
                    <SelectItem value={DEFAULT_SHIPPING_METHOD_ID}>Default</SelectItem>
                    {filteredShippingMethods?.map((shippingMethod) => (
                      <SelectItem key={shippingMethod.id} value={shippingMethod.id}>
                        {renderShippingMethodName(shippingMethod)}
                      </SelectItem>
                    ))}
                  </SelectFormField>
                  <SelectFormField
                    control={form.control}
                    name="signatureRequired"
                    label="Signature required"
                    className="w-full min-w-0"
                    disabled={!isHydrated}
                  >
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectFormField>
                  <SelectFormField
                    control={form.control}
                    name="incoTerms"
                    label="Inco terms"
                    className="w-full min-w-0"
                    disabled={!isHydrated}
                  >
                    <SelectItem value={IncoTerms.DDU}>DDU</SelectItem>
                    <SelectItem value={IncoTerms.DDP}>DDP</SelectItem>
                  </SelectFormField>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Export codes</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                  <TextFormField
                    control={form.control}
                    name="primaryCode"
                    label="Primary export code"
                    disabled={!isHydrated}
                  />
                  <TagsFormField
                    control={form.control}
                    name="secondaryCodes"
                    label="Import codes"
                    disabled={!isHydrated}
                    caseSensitive
                  />
                </CardContent>
              </Card>
            </form>
          </Form>
        </DetailMain>
      </PageContent>
    </Page>
  );
}
